/* eslint-disable @opendoor/no-html-elements */
/* eslint-disable jsdoc/require-jsdoc */
import React from 'react';

import { media } from '@opendoor/bricks/theme/legacy/foundations/mediaBreakpoints';
import {
  colors,
  heightsWithUnits,
  spaceWithUnits,
  fontSizesNamedWithUnits,
} from '@opendoor/bricks/theme/eero';
import { NavCloseIcon, NavHamburgerIcon } from '@opendoor/bricks/deprecated/UNSAFE_icons';
import { hiddenXsDown } from '@opendoor/bricks/system/hidden';
import Responsive, { IBreakpointState } from '@opendoor/bricks/deprecated/UNSAFE_Responsive';
import ImgixImg from '@opendoor/bricks/utils/ImgixImg';
import { Box, Flex } from '@opendoor/bricks/core';
import { UNSAFE_Arrowhead } from '@opendoor/bricks/deprecated/UNSAFE_Arrow';
import styled from '@emotion/styled';
import { ExternalLink } from 'platform/react-router';

export interface IProps {
  /**
   * Name of the header in analytics events, should be unique within the app
   * Convention: {appName}-{headerName}
   */
  analyticsName: string;
  hiddenMobile?: boolean;
  hideAccount?: boolean;
  hideNav?: boolean;
  showBrokerageLogo?: boolean;
  hideBorder?: boolean;
  mobileNavVisible?: boolean;
  hasRenderedNav?: boolean;
  onBackClick?: () => void;
  renderAccountMenu?: () => JSX.Element | null;
  renderLogo?: () => JSX.Element | null;
  renderExtra?: () => JSX.Element | null;
  hideMobileNav?: () => void;
  showMobileNav?: () => void;
}

interface IResponsiveProps {
  gtLG: IBreakpointState['gtLG']; // passed in from <ResponsiveHeader>
}

export const Header: React.FC<IProps & IResponsiveProps> = ({
  analyticsName,
  hiddenMobile,
  hideAccount,
  hideNav,
  hideBorder,
  showBrokerageLogo,
  mobileNavVisible,
  hasRenderedNav,
  renderAccountMenu,
  renderLogo,
  renderExtra,
  onBackClick,
  hideMobileNav,
  showMobileNav,
  gtLG,
}) => {
  const logo = getLogo({ renderLogo, showBrokerageLogo });
  const isOnSmallViewport = !gtLG;

  // Don't render the rest of the top-nav if the mobile-side-nav is visible, or if the back button is showing
  const dontRenderRestOfNav = (mobileNavVisible && isOnSmallViewport) || !!onBackClick;
  const canRenderRestOfNav = !dontRenderRestOfNav;

  const renderTopLeft = () => {
    return topLeftRenderer({
      analyticsName,
      logo,
      isOnSmallViewport,
      mobileNavVisible,
      hasRenderedNav,
      onBackClick,
      hideMobileNav,
      showMobileNav,
    });
  };

  return (
    <BaseHeader
      display={[hiddenMobile ? 'none' : 'flex', 'flex']}
      borderBottom={showBrokerageLogo && !hideBorder ? `3px solid ${colors.blue30}` : ''}
    >
      <div>{renderTopLeft()}</div>
      {canRenderRestOfNav && (
        <React.Fragment>
          {!hideNav && renderMiddle()}
          <Box visibility={hideAccount ? 'hidden' : 'visible'}>
            {renderAccountMenu && renderAccountMenu()}
          </Box>
          {renderExtra && renderExtra()}
        </React.Fragment>
      )}
    </BaseHeader>
  );
};

// TODO: @sellDirect - delete layout once legacy cake is deprecated
export const ResponsiveHeader: React.FC<IProps> = (props) => {
  // @ts-expect-error legacy code that will be deleted shortly - not fixing types for now
  const renderHeader = ({ gtLG }) => <Header {...props} gtLG={gtLG} />;
  return <Responsive render={renderHeader} />;
};
ResponsiveHeader.displayName = 'Header';

// TODO: @sellDirect - delete layout once legacy cake is deprecated
// @ts-expect-error legacy code that will be deleted shortly - not fixing types for now
const getLogo = ({ renderLogo, showBrokerageLogo }): JSX.Element | null => {
  if (renderLogo) {
    return renderLogo();
  }

  return showBrokerageLogo ? (
    <ImgixImg
      style={{ display: 'block', margin: '30px 0' }}
      src="logo-opendoor-homes.svg"
      alt="Opendoor Homes"
      height="25"
    />
  ) : (
    <a href="/">
      <img
        style={{ display: 'block', margin: '30px 0' }}
        src="https://imgdrop.imgix.net/logo-opendoor.svg"
        alt="Opendoor"
        height="22"
      />
    </a>
  );
};

const renderMiddle = () => (
  <div>
    <ul>
      <PrimaryNavItem>
        <MenuItem href="/">Sell</MenuItem>
      </PrimaryNavItem>
      <PrimaryNavItemAdditional>
        <MenuItem href="/w/homes">Buy</MenuItem>
      </PrimaryNavItemAdditional>
      <PrimaryNavItemAdditionalHiddenXsDown>
        <MenuItem href="/w/how-it-works">How it works</MenuItem>
      </PrimaryNavItemAdditionalHiddenXsDown>
      <PrimaryNavItemAdditionalHiddenXsDown>
        <MenuItem href="/w/pricing">Pricing</MenuItem>
      </PrimaryNavItemAdditionalHiddenXsDown>
      <PrimaryNavItemAdditional className="nav-item-hoverable-dropdown-container">
        <MenuItemForDropdown href="#">
          <span css={hiddenXsDown}>More</span>
        </MenuItemForDropdown>
        <NarrowPrimaryNavSubmenu>
          <PrimaryNavSubmenuItem>
            <PrimaryNavSubmenuAnchorTag href="/w/about">About us</PrimaryNavSubmenuAnchorTag>
          </PrimaryNavSubmenuItem>
          <PrimaryNavSubmenuItem>
            <PrimaryNavSubmenuAnchorTag href="/w/stories">Stories</PrimaryNavSubmenuAnchorTag>
          </PrimaryNavSubmenuItem>
          <PrimaryNavSubmenuItem>
            <PrimaryNavSubmenuAnchorTag href="/w/reviews">Reviews</PrimaryNavSubmenuAnchorTag>
          </PrimaryNavSubmenuItem>
          <PrimaryNavSubmenuItem>
            <PrimaryNavSubmenuAnchorTag href="/w/faq">FAQ</PrimaryNavSubmenuAnchorTag>
          </PrimaryNavSubmenuItem>
          <PrimaryNavSubmenuItem>
            <PrimaryNavSubmenuAnchorTag href="/jobs">Jobs</PrimaryNavSubmenuAnchorTag>
          </PrimaryNavSubmenuItem>
          <PrimaryNavSubmenuItem>
            <PrimaryNavSubmenuAnchorTag href="/w/resources">Resources</PrimaryNavSubmenuAnchorTag>
          </PrimaryNavSubmenuItem>
          <PrimaryNavSubmenuItem>
            <PrimaryNavSubmenuAnchorTag href="/w/blog">Blog</PrimaryNavSubmenuAnchorTag>
          </PrimaryNavSubmenuItem>
          <PrimaryNavSubmenuItem>
            <PrimaryNavSubmenuAnchorTag href="/w/agents">Agents</PrimaryNavSubmenuAnchorTag>
          </PrimaryNavSubmenuItem>
          <PrimaryNavSubmenuItem>
            <PrimaryNavSubmenuAnchorTag href="/w/vendor-partnerships">
              Vendors
            </PrimaryNavSubmenuAnchorTag>
          </PrimaryNavSubmenuItem>
          <PrimaryNavSubmenuItem>
            <PrimaryNavSubmenuAnchorTag href="/w/contact-us">Contact</PrimaryNavSubmenuAnchorTag>
          </PrimaryNavSubmenuItem>
        </NarrowPrimaryNavSubmenu>
      </PrimaryNavItemAdditional>
    </ul>
  </div>
);

interface ITopLeftRenderer {
  analyticsName: IProps['analyticsName'];
  isOnSmallViewport: boolean;
  mobileNavVisible: IProps['mobileNavVisible'];
  hasRenderedNav: IProps['hasRenderedNav'];
  logo: JSX.Element | null;
  onBackClick: IProps['onBackClick'];
  hideMobileNav: IProps['hideMobileNav'];
  showMobileNav: IProps['showMobileNav'];
}

const topLeftRenderer = ({
  analyticsName,
  logo,
  isOnSmallViewport,
  mobileNavVisible,
  hasRenderedNav,
  onBackClick,
  hideMobileNav,
  showMobileNav,
}: ITopLeftRenderer): JSX.Element | null => {
  if (onBackClick) {
    return (
      <ExternalLink
        onClick={onBackClick}
        fontWeight="bold"
        analyticsName={`${analyticsName}-back`}
        aria-label="Go back to last page."
      >
        <Flex alignItems="center">
          <Box mr={2}>
            <UNSAFE_Arrowhead rotationDirection="left" size={4} />
          </Box>
          Back
        </Flex>
      </ExternalLink>
    );
  } else if (isOnSmallViewport && hasRenderedNav) {
    if (mobileNavVisible) {
      return <NavCloseIcon cursor="pointer" onClick={hideMobileNav} />;
    }

    return <NavHamburgerIcon cursor="pointer" onClick={showMobileNav} />;
  }

  return logo;
};

const BaseHeader = styled(Box)`
  display: flex;
  align-items: center;
  height: ${heightsWithUnits.header};
  justify-content: space-between;
  margin: auto;
  max-width: 1600px;
  padding: 0 25px;
  position: relative;
  z-index: 15;
  ${media.smallerThan.XS} {
    height: 52px;
  }
`;

const PrimaryNavItem = styled.li`
  position: relative;
  display: inline-flex;
  height: ${heightsWithUnits.header};
  align-items: center;
  line-height: 25px;
  list-style: none;

  ${media.smallerThan.XS} {
    margin-left: ${spaceWithUnits[3]};
  }
`;

const PrimaryNavItemAdditional = styled(PrimaryNavItem)`
  margin-left: ${spaceWithUnits[8]};

  ${media.smallerThan.SM} {
    margin-left: ${spaceWithUnits[4]};
    height: 52px;
  }
`;

const PrimaryNavItemAdditionalHiddenXsDown = styled(PrimaryNavItemAdditional)`
  ${hiddenXsDown}
`;

export const PrimaryNavSubmenu = styled.ul`
  position: absolute;
  top: 75px;
  left: 50%;
  visibility: hidden;
  padding: 25px;
  background: ${colors.neutrals0};
  border-radius: 3px;
  text-align: left;
  opacity: 0;
  transition: all ease 0.3s;
  box-shadow: -5px 0 40px ${colors.neutrals40};

  /* https://emotion.sh/docs/nested */
  .nav-item-hoverable-dropdown-container:hover & {
    top: 68px;
    visibility: visible;
    opacity: 1;
  }

  ${media.smallerThan.SM} {
    left: inherit;
    right: 0;
  }

  /* triangle selector */
  &:before {
    content: '';
    position: absolute;
    top: -7px;
    left: 50%;
    width: 0;
    height: 0;
    margin: 0 0 0 -10px;
    border-left: 10px solid transparent;
    border-right: 10px solid transparent;
    border-bottom: 10px solid ${colors.neutrals0};

    ${media.smallerThan.SM} {
      right: 10px;
      left: inherit;
    }
  }
`;

export const NarrowPrimaryNavSubmenu = styled(PrimaryNavSubmenu)`
  width: 100px;
  margin: 0 0 0 -75px;

  ${media.smallerThan.SM} {
    margin: 0;
    right: -20px;
    top: 30px;
    width: 120px;
  }

  ${media.smallerThan.XS} {
    margin: 0;
    right: -30px;
    top: 30px;
    width: 120px;
  }
`;

const MenuItem = styled.a`
  /* https://emotion.sh/docs/nested */
  li.current & {
    color: ${colors.brand50};
  }

  position: relative;
  color: inherit;

  ${media.smallerThan.XS} {
    font-size: ${fontSizesNamedWithUnits.s0};
  }

  &:hover {
    color: ${colors.brand50};
  }
`;

const MenuItemForDropdown = styled(MenuItem)`
  &:after {
    content: '';
    position: absolute;
    top: 4px;
    right: -20px;
    width: 20px;
    height: 15px;
    background: url('https://imgdrop.imgix.net/dropdown.svg') center no-repeat;
    background-size: 10px auto;
    opacity: 0.7;

    ${media.smallerThan.SM} {
      top: -10px;
    }
  }

  /* https://emotion.sh/docs/nested */
  .nav-item-hoverable-dropdown-container:hover & {
    &:after {
      opacity: 1;
    }
  }
`;

const PrimaryNavSubmenuItem = styled.li`
  display: inline-block;
  padding: 0;
  width: 100%;
  margin-top: 10px;

  &:last-child {
    margin: 0;
    margin-top: 10px;
  }

  &:first-of-type {
    margin: initial;
  }
`;

const PrimaryNavSubmenuAnchorTag = styled.a`
  font-size: 15px;
  color: inherit;
  cursor: pointer;

  &:hover {
    color: ${colors.brand50};
  }
`;
